import { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { Tooltip } from '@odo/shared-ui';
import type { VirtualAppointmentOverviewItem } from '../../../services/virtualAppointmentsOverview';
import { useIsTextTruncated } from '../../../hooks/useIsTextTruncated';
import { Avatar } from '../../Avatar';
import { PracticeDoctorLabel } from '../../shared/PracticeDoctorLabel';
import { TruncatedLabel } from '../../shared/TruncatedLabel';
import {
    formatSummaryTrigger,
    fullTypeTitle
} from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { summaryFromDto } from '../../virtual-appointments/virtualAppointmentSummary';
import { TIER_TEXT_CLASS } from '../timeThresholds';
import { LATE_REASON_TEXT, lateDistance } from './lateDistance';
import { MONITORING_STATE_META, monitoringStateOf } from './monitoringState';
import { triggerHint } from './triggerHint';

interface ItemCellProps {
    item: VirtualAppointmentOverviewItem;
}

interface PatientCellProps extends ItemCellProps {
    doctorName: string | null;
}

const OUTSIDE_TREATMENT_TEXT =
    "Has an open or overdue VA but isn't in the Treatment phase, so no VA team is working it.";

function OutsideTreatmentWarning() {
    return (
        <Tooltip content={OUTSIDE_TREATMENT_TEXT}>
            <ExclamationTriangleIcon
                className="h-3.5 w-3.5 flex-shrink-0 text-amber-500"
                aria-label={OUTSIDE_TREATMENT_TEXT}
            />
        </Tooltip>
    );
}

export function OverviewPatientCell({ item, doctorName }: PatientCellProps) {
    const { patient } = item;
    return (
        <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 flex-shrink-0">
                <Avatar user={{ name: patient.name, email: patient.id }} />
            </div>
            <div className="flex flex-col min-w-0 leading-tight">
                <span className="flex items-center gap-1 min-w-0">
                    <TruncatedLabel
                        text={patient.name}
                        className="block min-w-0 text-xs font-medium leading-4 text-gray-800"
                    />
                    {patient.pendingVaOutsideTreatment && <OutsideTreatmentWarning />}
                </span>
                <PracticeDoctorLabel
                    practiceId={patient.practiceId}
                    practiceName={patient.practiceName}
                    doctorName={doctorName}
                    format="doctor-practice"
                    className="block truncate text-[10px] max-w-full mt-1"
                    toneClassName="text-gray-500"
                />
            </div>
        </div>
    );
}

// "Vrefi · Refinement check" on one line; the full title shows on hover only when it is cropped.
export function OverviewTypeCell({ item }: ItemCellProps) {
    const summary = summaryFromDto(item);
    const fullTitle = fullTypeTitle(summary, ' · ');
    const [element, setElement] = useState<HTMLSpanElement | null>(null);
    const truncated = useIsTextTruncated(element, fullTitle);
    return (
        <div className="flex flex-col min-w-0 leading-tight">
            <Tooltip content={fullTitle} disabled={!truncated}>
                <span ref={setElement} className="block truncate text-xs">
                    <span className="font-semibold text-gray-800">{summary.typeTitle}</span>
                    {summary.typeLabel && (
                        <span className="text-gray-500"> · {summary.typeLabel}</span>
                    )}
                </span>
            </Tooltip>
            {item.description && (
                <TruncatedLabel
                    text={item.description}
                    className="block text-[10px] text-gray-500 mt-1"
                />
            )}
        </div>
    );
}

interface MonitoringTagProps extends ItemCellProps {
    /** Off in the table, where the trigger's own late reason already owns the hover. */
    withTooltip?: boolean;
}

// Only while the VA still waits on its aligner: once reached or opened, monitoring no longer affects it.
export function MonitoringTag({ item, withTooltip = true }: MonitoringTagProps) {
    const waiting = item.status === 'planned' && !item.isDue;
    if (item.triggerKind !== 'aligner' || !waiting) return null;
    const { tag } = MONITORING_STATE_META[monitoringStateOf(item.patient.monitoringStatus)];
    if (!tag) return null;
    return (
        <Tooltip content={tag.tooltip} side="top" disabled={!withTooltip}>
            <span
                className={`inline-flex items-center shrink-0 rounded-full border px-1.5 py-px text-[10px] font-semibold whitespace-nowrap ${tag.className}`}
            >
                {tag.label}
            </span>
        </Tooltip>
    );
}

interface TriggerCellProps extends ItemCellProps {
    todayIso: string;
}

// A stuck VA (see LATE_REASON_TEXT) takes the work queue's lateness colours, and its hint says
// how long ago the trigger was reached; that colour stands in for a Due pill.
export function OverviewTriggerCell({ item, todayIso }: TriggerCellProps) {
    const late = lateDistance(item, todayIso);
    const hint = late ? (late.ago ?? 'late') : triggerHint(item, todayIso);
    const tone = late ? TIER_TEXT_CLASS[late.tier] : 'text-gray-800';
    return (
        <Tooltip content={item.late ? LATE_REASON_TEXT[item.late.reason] : null} focusable>
            <div className="flex flex-col min-w-0 leading-tight">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`text-xs whitespace-nowrap ${tone}`}>
                        {formatSummaryTrigger(summaryFromDto(item))}
                    </span>
                    <MonitoringTag item={item} withTooltip={false} />
                </div>
                {hint && (
                    <span
                        className={`mt-1 truncate text-[10px] ${late ? TIER_TEXT_CLASS[late.tier] : 'text-gray-500'}`}
                    >
                        {hint}
                    </span>
                )}
            </div>
        </Tooltip>
    );
}
